import { Feature, FeatureCollection, Polygon } from "geojson";
import { useQuery, UseQueryOptions } from "react-query";
import getBasename from "../utilities/getBasename";

// interfaces
export interface HucFeatureProperties {
    fid: number;
    hucId: string;
    name: string;
    states: string;
    loaddate: string;
}

export interface UseFetchHucs {
    hucs: HucFeatures | undefined;
    getHuc: ((hucId: HucFeatureProperties["hucId"]) => HucFeature | undefined);
    getHucProps: ((hucId: HucFeatureProperties["hucId"]) => HucFeatureProperties | undefined);
}

// types
export type HucFeatures = FeatureCollection<Polygon, HucFeatureProperties>;
export type HucFeature = Feature<Polygon, HucFeatureProperties>;
export type UseHucsQueryOptions = Omit<UseQueryOptions<HucFeatures, undefined, HucFeatures, string>, 'queryKey' | 'queryFn' | 'enabled'>;

// helpers
export async function getHucData(filename: string): Promise<HucFeatures> {
    const res = await fetch(filename);
    return (await res.json() as Promise<HucFeatures>);
}


// hooks
export function useFetchHucs(filename: string, options?: UseHucsQueryOptions): UseFetchHucs {
    const cache_key = getBasename(filename); // guard and log

    cache_key.length === 0 && console.error(`useHucs hook: empty filename provided as input`);

    const onError = () => console.error(`useHucs hook: error retrieving ${filename}`);

    const {
        data: hucs, isLoading
    } = useQuery(cache_key, () => getHucData(filename), {
        staleTime: Infinity,
        cacheTime: Infinity,
        onError: onError,
        ...options
    });

    function getHuc(hucId: string): HucFeature | undefined {
        // guard if still fetching
        if (isLoading) {
            console.info(`useHucs hook still loading. Method getHuc, did not return a feature for hucId ${hucId}`);
            return undefined
        }
        const d = hucs?.features.filter((item: HucFeature) => item.properties.hucId === hucId);

        if (d === undefined || d.length === 0) {
            console.error(`useHucs hook method getHuc, did not return a feature for hucId ${hucId}`);
            return undefined;
        }

        if (d.length > 1) {
            console.error(`useHucs hook method getHuc, returned more than one feature for hucId ${hucId}`);
            return undefined;
        }

        return d[0];
    }

    function getHucProps(hucId: string): HucFeatureProperties | undefined {
        return getHuc(hucId)?.properties;
    }

    return { hucs, getHuc, getHucProps };
}

export default useFetchHucs;
